// src/components/dashboard/CheckInTimeChart.js
import React, { useMemo, useState } from 'react';

/**
 * Check-In Time Distribution Chart
 * Histogram showing when customers check in for test drives
 *
 * @param {Array} data - Array of hourly data with { hour, count }
 */
const CheckInTimeChart = ({ data }) => {
  const [hoveredBar, setHoveredBar] = useState(null);

  // Calculate max value for scaling
  const maxValue = useMemo(() => {
    return Math.max(...data.map(d => d.count));
  }, [data]);

  // Calculate height percentage for bars
  const getHeight = (value) => {
    if (maxValue === 0) return 0;
    return (value / maxValue) * 100;
  };

  // Find peak hours
  const peakHour = useMemo(() => {
    return data.reduce((max, d) => d.count > max.count ? d : max, data[0]);
  }, [data]);

  return (
    <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary border border-app-border-light dark:border-app-border rounded-2xl p-4 lg:p-6">
      <div className="mb-4">
        <h3 className="text-base lg:text-lg font-semibold text-app-text-light dark:text-app-text">
          Check-In Time Distribution
        </h3>
        <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mt-1">
          Popular check-in hours • Peak: {peakHour.hour}
        </p>
      </div>

      {/* Chart */}
      <div className="flex items-end gap-1 lg:gap-2" style={{ height: '200px' }}>
        {data.map((item, index) => {
          const height = getHeight(item.count);
          const isHovered = hoveredBar === index;
          const isPeak = item.hour === peakHour.hour;

          return (
            <div
              key={item.hour}
              className="flex-1 flex flex-col items-center justify-end"
              onMouseEnter={() => setHoveredBar(index)}
              onMouseLeave={() => setHoveredBar(null)}
            >
              {/* Tooltip */}
              {isHovered && (
                <div className="absolute mb-2 transform -translate-y-full">
                  <div className="bg-app-bg-secondary-light dark:bg-app-bg-tertiary border border-app-border-light dark:border-app-border rounded-lg p-2 shadow-lg whitespace-nowrap">
                    <div className="text-xs font-semibold text-app-text-light dark:text-app-text">
                      {item.hour}
                    </div>
                    <div className="text-xs text-purple-600 dark:text-purple-400">
                      {item.count} check-ins
                    </div>
                  </div>
                </div>
              )}

              {/* Bar */}
              <div className="relative w-full flex flex-col items-center">
                {/* Count label on top of bar */}
                {(item.count > 0 && (isPeak || isHovered)) && (
                  <div className="text-[10px] font-semibold text-purple-600 dark:text-purple-300 mb-1">
                    {item.count}
                  </div>
                )}

                {/* Bar itself */}
                <div
                  className={`w-full rounded-t transition-all ${
                    isPeak
                      ? 'bg-purple-500 border-2 border-purple-400'
                      : isHovered
                      ? 'bg-purple-400 border-2 border-purple-300'
                      : 'bg-purple-300 dark:bg-purple-500/50 border border-purple-400 dark:border-purple-500/30'
                  }`}
                  style={{ height: `${height}%`, minHeight: item.count > 0 ? '4px' : '0' }}
                />
              </div>

              {/* Hour label */}
              <div className={`text-[9px] lg:text-[10px] mt-2 transition-colors ${
                isPeak
                  ? 'text-purple-600 dark:text-purple-300 font-bold'
                  : isHovered
                  ? 'text-purple-500 dark:text-purple-400'
                  : 'text-app-text-secondary-light dark:text-app-text-secondary'
              }`}>
                {item.hour.replace(' ', '')}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary stats */}
      <div className="mt-6 pt-4 border-t border-app-border-light dark:border-app-border">
        <div className="grid grid-cols-3 gap-3 text-center text-xs">
          <div>
            <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
              {data.reduce((sum, d) => sum + d.count, 0)}
            </div>
            <div className="text-[10px] text-app-text-secondary-light dark:text-app-text-secondary">Total Check-Ins</div>
          </div>
          <div>
            <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
              {peakHour.hour}
            </div>
            <div className="text-[10px] text-app-text-secondary-light dark:text-app-text-secondary">Peak Hour</div>
          </div>
          <div>
            <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
              {peakHour.count}
            </div>
            <div className="text-[10px] text-app-text-secondary-light dark:text-app-text-secondary">Peak Count</div>
          </div>
        </div>
      </div>

      {/* Time period insights */}
      <div className="mt-3 pt-3 border-t border-app-border-light dark:border-app-border">
        <div className="text-[10px] text-app-text-secondary-light dark:text-app-text-secondary mb-2">Busiest Periods:</div>
        <div className="flex flex-wrap gap-2">
          {data
            .filter(d => d.count >= maxValue * 0.7)
            .map((d, i) => (
              <div
                key={i}
                className="px-2 py-1 bg-purple-100 dark:bg-purple-500/20 border border-purple-200 dark:border-purple-500/30 rounded text-[10px] text-purple-600 dark:text-purple-300"
              >
                {d.hour}: {d.count}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default CheckInTimeChart;
